import { Directive, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { SubscriptionLike } from 'rxjs';

@Directive({
  selector: '[digitalPlatformZipFormat]',
})
export class ZipDirective implements OnChanges, OnInit, OnDestroy {
  @Input() private digitalPlatformZipFormat = 'US';

  private previous: string;
  private subscription: SubscriptionLike;

  constructor(public ngControl: NgControl) {
    this.previous = this.ngControl.value || '';
  }

  ngOnChanges() {
    this.digitalPlatformZipFormat = this.digitalPlatformZipFormat || 'US';
  }

  ngOnInit() {
    this.subscription = this.ngControl.valueChanges.subscribe((data) =>
      this.onInputChange(data)
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onInputChange(event: string) {
    if (!event) return;
    if (event === this.previous) return;

    let newVal = event;
    switch (this.digitalPlatformZipFormat) {
      case 'US':
      case 'MX':
        newVal = newVal.replace(/\D/g, '');
        break;
      case 'CA':
        newVal = newVal.toUpperCase().replace(/[^A-Z0-9]/g, '');
        break;
    }

    const formatted = this.format(newVal);

    // write raw value to form control
    this.ngControl.control.setValue(formatted, { emitEvent: false });

    // write formatted value to DOM & store for next run
    this.ngControl.valueAccessor.writeValue(formatted);
    this.previous = formatted;
  }

  format(value: string): string {
    switch (this.digitalPlatformZipFormat) {
      case 'US':
        return this.formatUSPostalCode(value);
      case 'CA':
        return this.formatCAPostalCode(value);
      case 'MX':
        return this.formatMXPostalCode(value);
    }

    return value;
  }

  formatUSPostalCode(value: string): string {
    if (value.length === 0) {
      return '';
    } else if (value.length <= 5) {
      return value.replace(/^(\d{0,5})/, '$1');
    } else if (value.length <= 9) {
      return value.replace(/^(\d{0,5})(\d{0,4})/, '$1-$2');
    } else {
      value = value.substring(0, 9);
      return value.replace(/^(\d{0,5})(\d{0,4})/, '$1-$2');
    }
  }

  // I feel like there's a better way to do this that's not so verbose -TS
  formatCAPostalCode(value: string): string {
    const parts = ['[A-Z]', '[0-9]', '[A-Z]', '[0-9]', '[A-Z]', '[0-9]'];

    const formatted = [...value]
      .map((char, i) => {
        if (i >= parts.length) {
          return '';
        }

        return char.match(parts[i]) ? char : '';
      })
      .join('');

    if (formatted.length === 0) {
      return '';
    } else if (formatted.length === 1) {
      return formatted.replace(/^([A-Z])/, '$1');
    } else if (formatted.length === 2) {
      return formatted.replace(/^([A-Z][0-9])/, '$1');
    } else if (formatted.length === 3) {
      return formatted.replace(/^([A-Z][0-9][A-Z])/, '$1');
    } else if (formatted.length === 4) {
      return formatted.replace(/^([A-Z][0-9][A-Z])([0-9])/, '$1 $2');
    } else if (formatted.length === 5) {
      return formatted.replace(/^([A-Z][0-9][A-Z])([0-9][A-Z])/, '$1 $2');
    } else if (formatted.length === 6) {
      return formatted.replace(/^([A-Z][0-9][A-Z])([0-9][A-Z][0-9])/, '$1 $2');
    } else {
      return formatted.replace(
        /^([A-Z][0-9][A-Z])([0-9][A-Z][0-9]).*/,
        '$1 $2'
      );
    }
  }

  formatMXPostalCode(value: string): string {
    if (value.length === 0) {
      return '';
    } else if (value.length <= 5) {
      return value.replace(/^(\d{0,5})/, '$1');
    } else {
      value = value.substring(0, 5);
      return value.replace(/^(\d{0,5})/, '$1');
    }
  }
}
